import React from "react";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiSearch, FiChevronLeft, FiChevronRight } from "react-icons/fi";

/**
 * SearchPager
 * mode:
 *  - "search": search bar + stats only
 *  - "pager":  pager only (arrows + Page x/y), no search
 *  - "both":   search + pager (pager auto-hides if not needed)
 */
export default function SearchPager({
  mode = "both",
  title = null,

  // Search
  query = "",
  onQueryChange = () => {},

  // Paging
  page = 1,
  pageSize = 10,
  totalItems = 0,
  filteredItems = 0,
  onPrev = () => {},
  onNext = () => {},

  // Optional: render something next to pager (e.g., Back to top link)
  rightExtra = null,
}) {
  const uoRed = "#8F001A";
  const border = useColorModeValue("gray.200", "gray.700");
  const bg = useColorModeValue("white", "gray.800");
  const helpText = useColorModeValue("gray.600", "gray.400");

  const totalPages = Math.max(1, Math.ceil((filteredItems || 0) / (pageSize || 1)));
  const safePage = Math.min(Math.max(1, page || 1), totalPages);

  const showPager = (filteredItems || 0) > (pageSize || 0);
  const canPrev = safePage > 1;
  const canNext = safePage < totalPages;

  const startIdx = (filteredItems || 0) === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endIdx = Math.min(safePage * pageSize, filteredItems || 0);

  const wantSearch = mode === "search" || mode === "both";
  const wantPager = mode === "pager" || mode === "both";
  const showStats = wantSearch; // stats most natural with search

  // If mode is "pager", hide the whole component when pager not needed
  if (mode === "pager" && !showPager) return null;

  return (
    <Box
      bg={bg}
      border={`1px solid ${border}`}
      borderRadius="16px"
      px={{ base: 4, md: 5 }}
      py={{ base: 4, md: 4 }}
      mb={5}
    >
      {/* ROW 1: Title + Stats (left)  |  Pager (+ optional extra) (right) */}
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap={3}
      >
        <Box>
          {title ? (
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="800"
              textDecoration="underline"
              textUnderlineOffset="4px"
              textDecorationThickness="2px"
              lineHeight="1.2"
            >
              {title}
            </Text>
          ) : null}

          {showStats && (
            <Text mt={title ? 1 : 0} color={helpText} fontSize="sm">
              {(filteredItems || 0) === 0
                ? "No items found."
                : `Showing ${startIdx}–${endIdx} of ${filteredItems} (Total: ${totalItems})`}
            </Text>
          )}
        </Box>

        {/* Right: pager + optional extra */}
        <HStack
          justify={{ base: "flex-start", md: "flex-end" }}
          spacing={3}
          flexWrap="wrap"
        >
          {wantPager && showPager && (
            <HStack spacing={2}>
              <Tooltip label="Previous" hasArrow>
                <IconButton
                  aria-label="Previous"
                  icon={<FiChevronLeft />}
                  onClick={onPrev}
                  isDisabled={!canPrev}
                  variant="outline"
                  borderRadius="999px"
                  size="sm"
                />
              </Tooltip>

              <Text fontSize="sm" minW="110px" textAlign="center">
                Page{" "}
                <Text as="span" fontWeight="700">
                  {safePage}
                </Text>{" "}
                / {totalPages}
              </Text>

              <Tooltip label="Next" hasArrow>
                <IconButton
                  aria-label="Next"
                  icon={<FiChevronRight />}
                  onClick={onNext}
                  isDisabled={!canNext}
                  variant="outline"
                  borderRadius="999px"
                  size="sm"
                  _hover={{ borderColor: uoRed, color: uoRed }}
                />
              </Tooltip>
            </HStack>
          )}

          {rightExtra}
        </HStack>
      </Flex>

      {/* ROW 2: Search UNDER title/stats */}
      {wantSearch && (
        <Box mt={3}>
          <InputGroup maxW={{ base: "100%", md: "520px" }}>
            <InputLeftElement pointerEvents="none" color={helpText}>
              <FiSearch />
            </InputLeftElement>
            <Input
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search by keywords..."
              borderRadius="999px"
            />
          </InputGroup>
        </Box>
      )}
    </Box>
  );
}